// LaTeX in answers (remark-math + rehype-katex), without breaking prices.
//
// Finance text is full of dollar amounts ("between $725 and $769"), so the
// usual single-$ inline math would turn prose into formulas. remark-math runs
// with singleDollarTextMath off; this normalises the delimiters agents
// actually write into the $$…$$ form it parses:
//   * $…$ only when the span holds a LaTeX command (\text, \frac, ^{…}, _{…})
//   * \( … \) inline and \[ … \] display
// Code spans and fences are left alone.

export const remarkMathOptions = { singleDollarTextMath: false };

const LATEX_HINT = /\\[a-zA-Z]+|[\^_]\{/;

function normaliseProse(s: string): string {
  return s
    .replace(/\\\[([\s\S]+?)\\\]/g, (_, m) => `\n$$\n${m.trim()}\n$$\n`)
    .replace(/\\\((.+?)\\\)/g, (_, m) => `$$${m}$$`)
    .replace(/[^\n]+/g, inlineDollars);
}

/** Single-$ spans on one line: math only when the span holds LaTeX. A `$`
 *  that doesn't open math (a price) is kept, and scanning moves past it, so
 *  "$5 per share, $\\alpha$" still finds the second span. */
function inlineDollars(line: string): string {
  let out = "";
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    const dbl = line[i + 1] === "$" || line[i - 1] === "$";
    if (ch === "$" && !dbl && line[i - 1] !== "\\") {
      const j = line.indexOf("$", i + 1);
      const m = j > i ? line.slice(i + 1, j) : "";
      if (j > i && line[j + 1] !== "$" && LATEX_HINT.test(m) && !/^\s|\s$/.test(m)) {
        out += `$$${m}$$`;
        i = j + 1;
        continue;
      }
    }
    out += ch;
    i++;
  }
  return out;
}

/** Rewrite math delimiters outside code so remark-math sees $$…$$ only. */
export function normaliseMath(text: string): string {
  if (!text || (!text.includes("$") && !text.includes("\\(") && !text.includes("\\["))) return text;
  // Split on fenced blocks and inline code; only prose segments are touched.
  const parts = text.split(/(```[\s\S]*?```|`[^`\n]*`)/g);
  return parts.map((p, i) => (i % 2 === 1 ? p : normaliseProse(p))).join("");
}
